import { RingModel } from "../Model/RingModel";
import { ringMaterialHelper } from "../Model/RingMaterials";
import * as THREE from "three";
import { IFRAssetProvider } from "../Controller/3DController/core/IFRAssetProvider";
import { Configuration, Engine, Grouping, IAsset, IGeometry, IMaterial, VisibilityGroup } from "@viscircle-org/threngine";


export class SceneGenerator {
  public config:Configuration;

  private _engine:Engine;
  private _assetProvider:IFRAssetProvider;

  private _rings:RingModel[];
  private _originalConfig:Configuration;
  private _assets:IAsset[];
  private _geometryAssets:IGeometry[];
  private _materialAssets:IMaterial[];
  private _ringScenes:THREE.Scene[] = [];

  constructor(engine:Engine, assets:IAsset[], originalConfig:Configuration, rings:RingModel[]) {
    this._engine = engine;
    this._assetProvider = (engine as any)._assetProvider;
    this._assets = assets;
    this._rings = rings;
    this._originalConfig = originalConfig;

    // console.log("SceneGenerator.assets", assets);
    // console.log("SceneGenerator._originalConfig", originalConfig);

    this.config = {
      default_state: {
        camera: originalConfig.default_state.camera,
        environment: originalConfig.default_state.environment,
        grouping_material: [],
        grouping_visible: [],
        material_groups: [],
        visibility_sets: [],
        animation_groups: [],
        render_settings: originalConfig.default_state.render_settings
      },
      groupings: [],
      hotspots: [],
      material_groups: [],
      version: '6',
      visibility_groups: [],
      animation_groups: [],
      visibility_sets: [],
      transforms: originalConfig.transforms
    }
  }

  public generateScene = ():Promise<Configuration> => {
    return new Promise((resolve, reject) => {
      //console.log("--- SceneGenerator.doTheThing() called! ---");

      this._geometryAssets = this._assets.filter(a => a.infoType === 'GEOMETRY') as IGeometry[];
      this._materialAssets = this._assets.filter(a => a.infoType === 'MATERIAL') as IMaterial[];

      //console.log("_assets", this._assets);
      //console.log("_geometryAssets", this._geometryAssets);
      //console.log("_materialAssets", this._materialAssets);
      //console.log("_rings", this._rings);

      const promises = [];

      this._rings.forEach((ring) => {
        promises.push(this._loadGeometryForRing(ring.SKU));
      });

      Promise.all(promises)
      .then(() => {
        //console.log('_ringScenes', this._ringScenes);
        //console.log(this._getGroupingsFromScene(this._ringScenes[0]));

        this._ringScenes.forEach((ringScene) => {
          this._applyRotation(ringScene);

          const groupings = this._getGroupingsFromScene(ringScene);

          const visibilityGroup:VisibilityGroup = {
            id: `VISIBILITY_GROUP_${ringScene.userData.ringSKU}`,
            name: ringScene.userData.ringSKU,
            grouping_ids: groupings.map(g => g.id)
          }

          this.config.groupings = this.config.groupings.concat(groupings);
          this.config.visibility_groups.push(visibilityGroup);
        })

        this._rings.forEach((ring) => {
          const groupings = this.config.groupings.filter(g => g.name.split('_')[0] === ring.SKU);

          groupings.forEach((g) => {
            let mat = this._materialAssets[0].id;
  
            if (g.name.includes('Engraving')) {
              mat = this._materialAssets.find((ma) => ma.info.name === 'Engraving').id;
            } 
  
            else if (g.name.includes('Logo')) {
              mat = this._materialAssets.find((ma) => ma.info.name === 'Engraving Logo').id;
            }

            else if (g.name.includes('Stone_Top')) {
              mat = this._materialAssets.find((ma) => ma.info.name === 'Diamond Top').id;
            }

            else if (g.name.includes('Zone')) {
              const metalZone = ring.metalZones.find(mz => mz.name === g.name);
  
              if (metalZone) {
                //console.log(g.name, metalZone);
                const matName = ringMaterialHelper.getMaterialName(metalZone.material, metalZone.surface);
                const foundMaterial = this._materialAssets.find((ma) => ma.info.name === matName);

                if (foundMaterial) {
                  mat = foundMaterial.id;
                } else {
                  console.error('Material not found!', matName);
                }
              } else {
                console.error('No metalZone data found for grouping!', g.name, ring);
              };
            }

            else if (g.name.includes('Stone')) {
              const stoneZone = ring.stoneZones.find(sz => sz.name === g.name);
  
              if (stoneZone) {
                //console.log(g.name, stoneZone);
                const matName = `${stoneZone.stone}`;
                const foundMaterial = this._materialAssets.find((ma) => ma.info.name === matName);

                if (foundMaterial) {
                  mat = foundMaterial.id;
                } else {
                  console.error('Material not found!', matName);
                }
              } else {
                console.error('No stoneZone data found for grouping!', g.name);
              };
            }

            else {
              console.error('Failed to handle grouping!', g);
            }
  
            this.config.default_state.grouping_material.push({
              id: g.id,
              material_id: mat
            })
          })
        })

        resolve(this.config);
      })
    })
  }

  private _loadGeometryForRing = async (SKU:string):Promise<void> => {
      const geometryAsset = this._geometryAssets.find((ga) => ga.info.name === SKU);
      if (!geometryAsset) {
        console.error('No geometry asset found: ' + SKU);
        return Promise.resolve(null); //we still want to continue, even if we're missing scenes for some rings
      }

      const scene = await this._engine.services.geometry.getScene(geometryAsset.id);

      // console.log(scene);

      scene.userData['ringSKU'] = SKU;
      this._ringScenes.push(scene);

      // console.log(this._ringScenes);

      return Promise.resolve(null);

      // resolve(geometryAsset);

      //OLD:
      // this._services.geometry.loadGeometry(geometryAsset.id)
      // .then(() => {
      //   return (this._services.config as any)._geometryHandler.get(geometryAsset.id)
      // })
      // .then((res) => {
      //   res.userData['ringSKU'] = SKU;
      //   this._ringScenes.push(res);
      //   resolve(res);
      // })
  }

  private _getGroupingsFromScene = (scene:THREE.Scene):Grouping[] => {
    const groupings:Grouping[] = [];

    const addToGrouping = (object:string, materialName:string, geometry:string) => {
      //If the grouping doesn't exist already we create it
      let grouping = groupings.find((g) => g.name===materialName && g.geometry_id===geometry);
      if (!grouping) {
        grouping = {
          id: `${geometry}_GROUPING${groupings.length}`,
          name: `${scene.userData['ringSKU']}_${materialName}`,
          object_names: [],
          geometry_id: geometry
        }
        groupings.push(grouping);
      }
      //--

      grouping.object_names.push(object);
    }

    scene.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (mesh && mesh.material && (mesh.type === 'Mesh')) {
        const geometry = mesh.userData['origin_id']; //ID of parent geometry
        let materialName = (mesh.material as THREE.Material).name;

        //HACKY FIX: remove Blender copy artifacts from material names. If material name ends with ".00#" remove its last 4 characters.
        if (materialName.substr(materialName.length-4, 3) === '.00') {
          materialName = materialName.slice(0, -4);
        };
        //--

        mesh.userData['ringSKU'] = scene.userData['ringSKU']

        addToGrouping(mesh.name, materialName, geometry);
      }
    });

    return groupings;
  };// _getGroupingsFromScene();

  private _applyRotation = (ringScene:THREE.Scene) => {
    const ringData = this._rings.find(r => r.SKU === ringScene.userData['ringSKU']);

    // window.setInterval(() => {
    //   ringScene.rotateX(THREE.Math.degToRad(0.2));
    // }, 25);

    ringScene.rotateX(THREE.MathUtils.degToRad(ringData.rotation.x));
  }

}// /class